"use client";
import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import { Calendar, CheckCircle2, AlertCircle, Wrench } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./allSection.css";
import axios from "axios";
import API_URL from "../../../src/Config";
import DashboardFilters from "../../Component/dashboard-filters";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="newdashboard-tooltip">
        <p className="newdashboard-tooltip-label">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export function PreventiveMaintenanceSection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState({
    total_scheduled_pm: { count: 0, label: "" },
    completed_pm: { count: 0, completion_rate: 0, label: "" },
    overdue_pm: { count: 0, label: "" },
    top_department: { name: "", scheduled_tasks: 0 },
  });

  const [charts, setCharts] = useState({
    pm_status_comparison: [],
    overdue_pm_trend: [],
    department_wise_status: [],
  });

  const [filters, setFilters] = useState({
    department_id: "",
    date_preset: "all",
    showCustomDate: false,
    start_date: "",
    end_date: "",
  });

  const fetchPMData = async (currentFilters = filters) => {
    try {
      setLoading(true);

      const authKey = localStorage.getItem("authKey");
      const params = new URLSearchParams();

      if (currentFilters.date_preset)
        params.append("filter_option", currentFilters.date_preset);

      if (currentFilters.department_id)
        params.append("department_id", currentFilters.department_id);

      if (currentFilters.start_date) {
        params.append("start_date", currentFilters.start_date);
      }
      if (currentFilters.end_date) {
        params.append("end_date", currentFilters.end_date);
      }

      const response = await axios.get(
        `${API_URL}/api/kpi/preventive-maintenance?${params.toString()}`,
        { headers: { Authorization: authKey } }
      );

      if (response.status === 200) {
        setSummary(response.data.summary || {});
        setCharts(response.data.charts || {});
      }
    } catch (err) {
      console.error("Error fetching PM KPIs:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    fetchPMData(newFilters);
  };

  const handleResetFilters = () => {
    const reset = {
      department_id: "",
      date_preset: "all",
      showCustomDate: false,
      start_date: "",
      end_date: "",
    };
    setFilters(reset);
    fetchPMData(reset);
  };

  useEffect(() => {
    fetchPMData();
  }, []);

  const handleCardClick = (listType) => {
    const params = new URLSearchParams();
    params.append("list_type", listType);
    
    // Ensure filter_option is lowercase and valid
    // const filterOptionValue = filters.date_preset ? filters.date_preset.toLowerCase() : "All";
    const filterOptionValue = filters.date_preset
    ? filters.date_preset
    : "All";
    params.append("filter_option", filterOptionValue);
    
    // Only add department_id if it exists and is not empty
    if (filters.department_id && filters.department_id.trim() !== "") {
      params.append("department_id", filters.department_id);
    }
    
    // Only add dates if both exist and are not empty
    if (filters.start_date && filters.end_date && 
        filters.start_date.trim() !== "" && filters.end_date.trim() !== "") {
      params.append("start_date", filters.start_date);
      params.append("end_date", filters.end_date);
    }

    console.log("Navigating to PM List with params:", params.toString());
    
    // Navigate in same tab
     window.open(`/pm-list?${params.toString()}`, "_blank");
    // navigate(`/pm-list?${params.toString()}`);
  };

  return (
    <div className="newdashboard-pm-section">
      {loading ? (
        <div className="loader-container">
          <div className="loader"></div>
          <div>Loading...</div>
        </div>
      ) : (
        <>
          <div className="newdashboard-pm-header">
            <div>
              <h2 className="newdashboard-section-title">
                Preventive Maintenance (PM)
              </h2>
              <p className="newdashboard-pm-description">
                Track scheduled maintenance tasks and completion rates
              </p>
            </div>

            <DashboardFilters
              filters={filters}
              onFilterChange={setFilters}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            />
          </div>

          {/* KPI Cards - Now Clickable */}
          <div className="newdashboard-kpi-grid">
            <div 
              className="newdashboard-card newdashboard-card-clickable"
              onClick={() => handleCardClick("scheduled")}
            >
              <div className="newdashboard-card-header">
                <h3 className="newdashboard-card-title">Total Scheduled PM</h3>
                <Calendar className="newdashboard-card-icon" />
              </div>
              <div className="newdashboard-card-content">
                <div className="newdashboard-card-value newdashboard-text-primary">
                  {summary.total_scheduled_pm.count}
                </div>
                <p className="newdashboard-card-subtitle">
                  {summary.total_scheduled_pm.label}
                </p>
              </div>
            </div>

            <div 
              className="newdashboard-card newdashboard-card-clickable"
              onClick={() => handleCardClick("completed")}
            >
              <div className="newdashboard-card-header">
                <h3 className="newdashboard-card-title">Completed PM</h3>
                <CheckCircle2 className="newdashboard-card-icon" />
              </div>
              <div className="newdashboard-card-content">
                <div className="newdashboard-card-value newdashboard-text-success">
                  {summary.completed_pm.count}
                </div>
                <p className="newdashboard-card-subtitle">
                  {summary.completed_pm.completion_rate}% completion rate
                </p>
              </div>
            </div>

            <div 
              className="newdashboard-card newdashboard-card-clickable"
              onClick={() => handleCardClick("overdue")}
            >
              <div className="newdashboard-card-header">
                <h3 className="newdashboard-card-title">Overdue PM</h3>
                <AlertCircle className="newdashboard-card-icon" />
              </div>
              <div className="newdashboard-card-content">
                <div className="newdashboard-card-value newdashboard-text-danger">
                  {summary.overdue_pm.count}
                </div>
              </div>
            </div>

            <div className="newdashboard-card">
              <div className="newdashboard-card-header">
                <h3 className="newdashboard-card-title">Top Department</h3>
                <Wrench className="newdashboard-card-icon" />
              </div>
              <div className="newdashboard-card-content">
                <div className="newdashboard-card-value-medium">
                  {summary.top_department.name}
                </div>
                <p className="newdashboard-card-subtitle">
                  {summary.top_department.scheduled_tasks} scheduled tasks
                </p>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="newdashboard-charts-grid">
            <div className="newdashboard-card">
              <div className="newdashboard-card-header-full">
                <h3 className="newdashboard-card-title">
                  PM Status Comparison
                </h3>
                <p className="newdashboard-card-description">
                  Scheduled vs Completed vs Overdue
                </p>
              </div>
              <div className="newdashboard-card-content">
                <div className="newdashboard-chart-container">
                  {charts.pm_status_comparison?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={charts.pm_status_comparison}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                        <YAxis stroke="#6b7280" fontSize={12} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="scheduled" fill="hsl(217, 91%, 60%)" />
                        <Bar dataKey="completed" fill="hsl(142, 71%, 45%)" />
                        <Bar dataKey="overdue" fill="hsl(0, 84%, 60%)" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="newdashboard-no-data">No Data Found</div>
                  )}
                </div>
              </div>
            </div>

            <div className="newdashboard-card">
              <div className="newdashboard-card-header-full">
                <h3 className="newdashboard-card-title">Overdue PM Trend</h3>
                <p className="newdashboard-card-description">
                  Track overdue tasks over time
                </p>
              </div>
              <div className="newdashboard-card-content">
                <div className="newdashboard-chart-container">
                  {charts.overdue_pm_trend?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={charts.overdue_pm_trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                        <YAxis stroke="#6b7280" fontSize={12} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="overdue"
                          stroke="hsl(0, 84%, 60%)"
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="newdashboard-no-data">No Data Found</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="newdashboard-card">
            <div className="newdashboard-card-header-full">
              <h3 className="newdashboard-card-title">
                Department-wise PM Status
              </h3>
              <p className="newdashboard-card-description">
                Breakdown by department
              </p>
            </div>
            <div className="newdashboard-card-content">
              <div className="newdashboard-chart-container">
                {charts.department_wise_status?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={charts.department_wise_status}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="department"
                        stroke="#6b7280"
                        fontSize={12}
                      />
                      <YAxis stroke="#6b7280" fontSize={12} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="scheduled" fill="hsl(217, 91%, 60%)" />
                      <Bar dataKey="completed" fill="hsl(142, 71%, 45%)" />
                      <Bar dataKey="overdue" fill="hsl(0, 84%, 60%)" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="newdashboard-no-data">No Data Found</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}